/**
 * Module Client #10 — Profil, 2FA & collaborateurs
 * Source : Guide_Client_Baobab_Connect.pdf · Chapitres 1, 5, 7, 8
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const IMG = (name) => `${BACKEND}/api/uploads/docs/html_assets/client/${name}`;

const moduleClientProfile = {
  id: 'client-documentation',
  population: 'client',
  order: 10,
  category: 'Support',
  title: 'Profil, sécurité 2FA & collaborateurs',
  subtitle: 'Sécuriser votre compte et gérer votre équipe en autonomie',
  icon: 'Shield',
  duration: '20 min',
  level: 'Débutant → Intermédiaire',
  audience: 'Administrateurs entreprise',
  slides: [
    { type: 'cover', eyebrow: 'Module 10 — Client', title: 'Profil & Sécurité', subtitle: 'Maîtriser votre compte, la 2FA et l\'équipe', icon: 'Shield', duration: '20 min', audience: 'Admins entreprise', level: 'Intermédiaire' },
    { type: 'intro', title: 'Objectifs', intro: 'Sécurisez votre compte et collaborez en équipe en toute conformité RGPD.', objectives: [
      'Compléter votre profil entreprise (NCC, RC, DFE, adresse)',
      'Activer la double authentification (2FA) en 3 minutes',
      'Gérer plusieurs collaborateurs avec rôles distincts',
      'Configurer vos préférences de notifications (email/SMS/WhatsApp)',
      'Comprendre les permissions RBAC (Admin, Gestionnaire, Lecteur)',
      'Désactiver un collaborateur quittant l\'entreprise',
    ]},
    { type: 'screenshot', eyebrow: 'Activation 2FA', title: 'Configurer la double authentification',
      image: IMG('img_108_2fa_qr.png'),
      callouts: [
        { label: 'QR Code', text: 'Scannez avec Google Authenticator, Authy, ou Microsoft Authenticator.', x: 50, y: 35 },
        { label: 'Clé manuelle', text: 'Si pas de smartphone, copiez la clé alphanumérique.', x: 50, y: 65 },
        { label: 'Code de vérification', text: 'Saisissez le code 6 chiffres généré par l\'app pour confirmer.', x: 50, y: 85 },
      ],
    },
    { type: 'steps', title: 'Activer la 2FA en 3 minutes', steps: [
      { title: 'Ouvrir Paramètres → Sécurité', detail: 'Avatar en haut à droite → Paramètres.' },
      { title: 'Cliquer sur « Activer 2FA »', detail: 'Le QR code et la clé manuelle s\'affichent.' },
      { title: 'Installer une app TOTP', detail: 'Google Authenticator, Authy, ou Microsoft Authenticator (gratuites).' },
      { title: 'Scanner le QR code', detail: 'L\'app génère un code à 6 chiffres rafraîchi toutes les 30s.' },
      { title: 'Saisir le code dans le formulaire', detail: 'Validation immédiate de l\'activation.' },
      { title: 'Sauvegarder les codes de récupération', detail: 'À copier dans un coffre-fort en cas de perte du téléphone.' },
    ]},
    { type: 'concept', eyebrow: 'RBAC', icon: 'Users', title: 'Les 3 rôles dans votre entreprise',
      lede: 'Vous pouvez attribuer un de ces 3 rôles à chaque collaborateur :',
      bullets: [
        '👑 Admin : tous les droits — créer, modifier, payer, gérer l\'équipe',
        '⚙️ Gestionnaire : créer/suivre les opérations mais sans modifier l\'équipe ni payer',
        '👁️ Lecteur : consulter uniquement, exports CSV autorisés',
        'Chaque action est tracée avec l\'identité du collaborateur (audit).',
      ],
      note: 'Vous pouvez créer un rôle personnalisé via Paramètres → Permissions (RBAC V2).',
    },
    { type: 'steps', title: 'Inviter un collaborateur', steps: [
      { title: 'Paramètres → Équipe → « Inviter »', detail: 'Bouton vert en haut à droite.' },
      { title: 'Saisir l\'email du collaborateur', detail: 'Doit être unique sur la plateforme.' },
      { title: 'Choisir le rôle', detail: 'Admin / Gestionnaire / Lecteur (peut être changé après).' },
      { title: 'Envoyer l\'invitation', detail: 'Email automatique avec lien d\'activation valable 7 jours.' },
      { title: 'Le collaborateur active son compte', detail: 'Définit son mot de passe et active sa 2FA recommandée.' },
    ]},
    { type: 'checklist', title: 'Check-list sécurité', items: [
      'Mon profil entreprise est complet à 100 % (NCC, RC, DFE)',
      'La 2FA est activée sur mon compte',
      'Les codes de récupération 2FA sont sauvegardés',
      'Chaque collaborateur a son propre compte (pas de partage)',
      'Les rôles sont attribués selon le besoin réel',
      'Les anciens collaborateurs sont désactivés (pas supprimés — traçabilité)',
    ]},
    { type: 'comparison', title: 'Sécurité : bonnes pratiques vs erreurs', do: [
      'Activer la 2FA dès le 1er jour',
      'Mots de passe uniques de 12+ caractères',
      'Désactiver les collaborateurs qui partent (pas supprimer)',
      'Vérifier les sessions actives régulièrement',
    ], dont: [
      'Ne partagez pas votre compte (chaque user = compte propre)',
      'N\'envoyez pas vos identifiants par email/WhatsApp',
      'N\'utilisez pas le même mot de passe ailleurs',
      'Ne désactivez pas la 2FA "pour aller plus vite"',
    ]},
    { type: 'quiz', title: 'Quiz #1',
      question: 'Vous perdez votre téléphone avec votre app TOTP. Que se passe-t-il si vous n\'avez pas sauvegardé vos codes de récupération ?',
      options: [
        'Le système me laisse passer une fois sans 2FA',
        'Je dois contacter le support pour vérification d\'identité (24-48 h)',
        'Mon compte est définitivement perdu',
        'Je désactive la 2FA depuis un autre appareil',
      ],
      answerIndex: 1,
      explanation: 'Sans codes de récupération, seul le support peut réinitialiser après KYC. D\'où l\'importance de SAUVEGARDER ces codes dès l\'activation.',
    },
    { type: 'quiz', title: 'Quiz #2',
      question: 'Un collaborateur quitte l\'entreprise. Que faire ?',
      options: [
        'Supprimer son compte définitivement',
        'Le désactiver (statut Inactif) — son historique reste tracé',
        'Changer son mot de passe',
        'Ne rien faire',
      ],
      answerIndex: 1,
      explanation: 'La désactivation bloque l\'accès tout en conservant l\'historique. La suppression complète casse la traçabilité OHADA.',
    },
    { type: 'takeaways', title: 'À retenir', items: [
      'La 2FA divise par 100 le risque de piratage.',
      'Codes de récupération = sauvegarde obligatoire.',
      'Chaque collaborateur = compte propre, jamais partagé.',
      'Désactiver ≠ supprimer (conservation historique).',
      'Audit complet de toutes les actions par utilisateur.',
    ]},
    { type: 'cta', title: 'Module 10 terminé !', subtitle: 'Votre compte et votre équipe sont sécurisés.',
      actions: [
        { label: 'Mes paramètres', href: '/paramètres' },
        { label: 'Catalogue formations', href: '/docs/client/training' },
      ],
    },
  ],
};

export default moduleClientProfile;
